import React, { useCallback, useEffect, useRef, useState } from "react";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import useAuthStore from "../../../Zustand/Store/useAuthStore.js";
import axios from "axios";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls.js";
import Layout4 from "../../DataLayouts/Layout4.jsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DateRangeIcon } from "@mui/x-date-pickers";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import TableDataGeneric from "../../../Configurations/TableDataGeneric.js";
import { fetchInternCertificates } from "../../../Apis/InternManagement.js";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { fetchOrganizationEmployee } from "../../../Apis/Employee-api";
import {
  applyClientToolbarFilters,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "intern_name",
  sortOrder: "desc",
};

const CERT_TOOLBAR_FILTERS = [
  { urlKey: "certificate_type", field: "certificate_type" },
  {
    urlKey: "issued_by",
    field: "issued_by_organization_person_id",
  },
];

const CERTIFICATE_TYPE_OPTIONS = [
  { value: "Completion", label: "Completion" },
  { value: "Recommendation", label: "Recommendation" },
  { value: "Appreciation", label: "Appreciation" },
  { value: "Custom", label: "Custom" },
];

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });
  return filters;
};

const DEFAULT_COLUMNS = [
  {
    field: "intern_code",
    label: "intern_code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "Intern_name",
    label: "Intern_name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "certificate_number",
    label: "certificate_number",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "issue_date",
    label: "issude_date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

function InternCertificateList() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const [issuers, setIssuers] = useState([]);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  const navigate = useNavigate();

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `intern_certificates_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    fetchOrganizationEmployee(org.organization_id)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setIssuers(
          list.map((emp) => ({
            value: emp.organization_person_id,
            label:
              emp.name ||
              `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
              String(emp.organization_person_id),
          }))
        );
      })
      .catch((error) => {
        console.error("Failed to load issued-by employees", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  // Load data via indexWithFilters (filtered API)
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          CERT_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "intern_name",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/intern-certificate/filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          const fallback = await fetchInternCertificates(org.organization_id);
          let a = fallback?.intership?.data || fallback?.data || [];
          if (!Array.isArray(a)) a = [];
          a = applyClientToolbarFilters(a, searchParams, CERT_TOOLBAR_FILTERS);
          data = a.map((item) => ({
            ...item,
            id: item?.intern_certificate_id,
            // Intern_name:
            //   `${item?.intern?.org_persons?.person?.first_name || ""} ${item?.intern?.org_persons?.person?.middle_name || ""} ${item?.intern?.org_persons?.person?.last_name || ""}`.trim(),
            intern_code: item?.person_code,
          }));
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const normalized = (Array.isArray(data) ? data : []).map((item) => ({
          ...item,
          id: item.intern_certificate_id ?? item.id,
          Intern_name: item.Intern_name ?? item.intern_name ?? "",
          intern_code: item.intern_code ?? "",
          certificate_number: item.certificate_number ?? "",
          issue_date: item.issue_date ?? "",
        }));

        setDocuments(normalized);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching intern certificates:", err);
        toast.error("Failed to load intern certificates");
        setDocuments([]);
        setPaginationData(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  let deleteStatus = async (id) => {
    try {
      const org_id = org.organization_id;
      const response = await axios.delete(
        `${MAIN_URL}/api/organizations/${org_id}/intern-certificate/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

       if (response.status === 200) {
        toast.success(response.data.message);
           } else {
        const errorMessage =
          response.data.message ||
          response.data.errors?.[Object.keys(response.data.errors)[0]]?.[0] ||
          "Failed to delete Intern Certificate";

        toast.error(errorMessage);
        console.warn("Deletion error:", response.status, response.data);
      }

    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Delete failed:", error);
      toast.error(
        error.response?.data?.error || "Failed to delete Attendance Status Type"
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/intern-certificate/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      console.log("Successfully deleted units-types with id:", id);
      return Promise.resolve();
    } catch (error) {
      console.error("Delete failed:", error);
      return Promise.reject(error);
    }
  };

  const handleEdit = useCallback(
    (item) => {
      navigate(`/intern/certificates/edit/${item.id}`);
    },

    [navigate]
  );


                 const handleShow = useCallback(
    (item) => {
      navigate(`/intern/certificates/view/${item.id}`)
    },
    [navigate],
  )


  return (
    <>
      <Layout4
        loading={loading}
        heading={"Intern Certificates"}
        btnName={"Add Certificate"}
        delete_action={"INTERN_CERTIFICATE_DELETE"}
        Data={documents}
        tableHeaders={[
          {
            name: "Document Name",
            value_key: "attendance_status_type_name",
            textStyle: "capitalize",
          },
          {
            name: "Status Type Code",
            value_key: "attendance_status_type_code",
            textStyle: "capitalize",
          },
          {
            name: "Description",
            value_key: "description",
            textStyle: "capitalize",
          },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Attendance Status Type",
          "Attendance Status Type",
          "Add Attendance Status Type",
          "Attendance Status Type",
        ]}
        Route={"/intern/certificates"}
        setData={setDocuments}
        DeleteFunc={deleteStatus}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="intern_certificates"
          primaryKey="intern_certificate_id"
          heading="Intern Certificate"
          data={documents}
          sortname="intern_name"
          showActions={true}
          Route="/intern/certificates"
          DeleteFunc={deleteStatus}
          handleShow={handleShow}
          EditFunc={handleEdit}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          filterModule="intern_certificates"
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Certificate Type"
                urlKey="certificate_type"
                allLabel="All certificate types"
                options={CERTIFICATE_TYPE_OPTIONS}
                valueKey="value"
                labelKey="label"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Issued By"
                urlKey="issued_by"
                allLabel="All issuers"
                options={issuers}
                valueKey="value"
                labelKey="label"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          edit_delete_action={["INTERN_CERTIFICATE_DELETE", "INTERN_CERTIFICATE_EDIT"]}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default InternCertificateList;
